import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import { BlackFullWidthBtn } from '../../shared/component/headless/button/BtnBody';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useLoadingStore } from '../../shared/store/loadingStore';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import deleteIcon from '../../asset/icon/delete.png';
import { useOnBoardingData } from '../../shared/hooks/useOnBoardingData';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import { UniversalCheckbox } from '../../shared/component/UniversalCheckbox';

interface ModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

const AgreePolicy: React.FC<ModalProps> = ({ setIsUserInput }) => {
  const { deferredSignup } = useAuth();
  const { isLoading } = useLoadingStore();
  const { clearModal } = useModalStore();
  const { onBoardingData, updateOnBoardingData } = useOnBoardingData();

  const modalRef = useRef<HTMLDivElement>(null);
  const [required_over14, setRequired_over14] = useState(true);
  const [required_terms, setRequired_terms] = useState(true);
  const [required_privacy, setRequired_privacy] = useState(true);
  const [optional_promotion, setOptional_promotion] = useState(false);
  const [requiredAgreeError, setRequiredAgreeError] = useState('');

  useEffect(() => {
    if (required_over14 || required_terms || required_privacy || optional_promotion) {
      setIsUserInput(true);
    } else {
      setIsUserInput(false);
    }
  }, [required_over14, required_terms, required_privacy, optional_promotion]);

  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
    }
  }, []);


  const checkAgree = (value: 'over14' | 'terms' | 'privacy' | 'promotion') => {
    if (value === 'over14') {
      setRequired_over14(!required_over14);
    } else if (value === 'terms') {
      setRequired_terms(!required_terms);
    } else if (value === 'privacy') {
      setRequired_privacy(!required_privacy);
    } else if (value === 'promotion') {
      setOptional_promotion(!optional_promotion);
    }
    if (isAllRequiredAgree()) {
      setRequiredAgreeError('');
    }
  }

  const isAllRequiredAgree = () => {
    return required_over14 && required_terms && required_privacy;
  }

  const handleSubmit = async () => {
    if (!isAllRequiredAgree()) {
      setRequiredAgreeError(getModalMessage('CONSENT_AGREE_ALL_ERROR'));
      return;
    } else {
      setRequiredAgreeError('');
    }
    if (onBoardingData == null) return;
    if (onBoardingData.signupArchiveType == null) return;
    updateOnBoardingData({ isAgreePolicy: true })
    await deferredSignup({
      email: onBoardingData.email,
      password: 'null',
      handle: onBoardingData.handle,
      over14: required_over14,
      agreeTos: required_terms,
      agreePrivacy: required_privacy,
      marketingEmailOptIn: optional_promotion,
      signupArchiveType: onBoardingData.signupArchiveType,
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isLoading) {
      return null;
    }
    switch (event.key) {
      case 'Enter':
        if (event.target instanceof HTMLButtonElement) return;
        handleSubmit();
        break;
      case 'Escape':
        clearModal();
        break;
      default:
        break;
    }
  };


  const moveToPolicy = (e: React.MouseEvent<HTMLSpanElement>, url: string) => {
    e.preventDefault();
    window.location.href = url;
  }

  return (
    <AgreePolicyComp ref={modalRef} onKeyDown={handleKeyDown} tabIndex={-1}>
      <HeaderRow>
        <Title>{getModalMessage('CONSENT_TITLE')}</Title>
        <UniversalIconBtn
          size="small"
          border={false}
          isTransparent
          hoverOpacity
          backgroundImg={deleteIcon}
          onClick={() => clearModal()}
        />
      </HeaderRow>
      <CheckboxComp>
        <UniversalCheckbox
          checked={required_over14}
          onChange={() => checkAgree('over14')}
        />
        {getModalMessage('CONSENT_REQUIRED_LABEL')} {getModalMessage('CONSENT_AGE')}
      </CheckboxComp>
      <CheckboxComp>
        <UniversalCheckbox
          checked={required_terms}
          onChange={() => checkAgree('terms')}
        />
        {getModalMessage('CONSENT_REQUIRED_LABEL')} {getModalMessage('CONSENT_TERMS_BEFORE')}<NavLink onClick={(e) => moveToPolicy(e, '/policy/terms')}>{getModalMessage('CONSENT_TERMS_LINK')}</NavLink>{getModalMessage('CONSENT_TERMS_AFTER')}
      </CheckboxComp>
      <CheckboxComp>
        <UniversalCheckbox
          checked={required_privacy}
          onChange={() => checkAgree('privacy')}
        />
        {getModalMessage('CONSENT_REQUIRED_LABEL')} {getModalMessage('CONSENT_PRIVACY_BEFORE')}<NavLink onClick={(e) => moveToPolicy(e, '/policy/privacy')}>{getModalMessage('CONSENT_PRIVACY_LINK')}</NavLink>{getModalMessage('CONSENT_PRIVACY_AFTER')}
      </CheckboxComp>
      <CheckboxComp>
        <UniversalCheckbox
          checked={optional_promotion}
          onChange={() => checkAgree('promotion')}
        />
        {getModalMessage('CONSENT_OPTIONAL_LABEL')} {getModalMessage('CONSENT_PROMO')}
      </CheckboxComp>
      {requiredAgreeError !== null && <ErrorText>{requiredAgreeError}</ErrorText>}
      <SubmitButton onClick={handleSubmit} disabled={isLoading > 0}>
        {getBtnName('AGREE')}
      </SubmitButton>
    </AgreePolicyComp>
  );
};

const AgreePolicyComp = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 17px 10px 10px 10px;
  outline: none;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 44px;
`;

const Title = styled.h2`
  min-width: 0;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_01_1};
`;

const SubmitButton = styled(BlackFullWidthBtn)`
  margin: 10px 0px 20px 0;
`;

const CheckboxComp = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 16px;
  user-select: none;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const NavLink = styled.span`
  text-decoration: underline;
  cursor: pointer;
  margin-left: 4px;
  margin-right: 4px;
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.color_alert_red};
  // min-height: 18px;
  ${({ theme }) => theme.typography.Body_04};
`;

export default AgreePolicy;
